import { layout } from 'thewest/App.routing'
import { createPageDefinition, RouteServices } from 'common/routing/page-definition'
import { metrics } from 'component-library/__styling/settings'
import { isFeatureEnabled } from 'common/feature-togglings/features'
import { customTopicRoutes } from '../topic/custom-topic-routes'
import { RouteResolution } from 'common/routing/route-resolver'
import { billboardLeaderboardMrec } from '../ad-units'
import { subscriberExclusive } from '../home/subscriber-exclusive'
import {
    getCountrymanRouteInfo,
    getCountrymanCuratedSection,
} from '../topic/custom/get-countryman-route-info'
import { getKalgoorlieMinerHomepage } from './get-kalgoorlie-miner-homepage'
import { TheWestSection } from 'component-library/__styling/settings/sections/thewest'

export interface RegionalHomepageOptions {
    section: TheWestSection
    mastheadTopic: string
    regionalTopic: string
    title: string
    description: string
    socialDescription: string
}

export const getRegionalHomepage = (
    services: RouteServices<RouteResolution>,
    options: RegionalHomepageOptions
) => {
    if (!isFeatureEnabled(services.store.getState().toggles, 'regionals')) {
        // DPT-1409: if the section is countryman, we need to return the existing page for it
        if (options.section === 'countryman') {
            return customTopicRoutes['/countryman'](services)
        }

        return null
    }

    // DPT-1494: Kalgoorlie Miner uses a custom curation on its homepage
    if (options.section === 'kalgoorlie-miner') {
        return getKalgoorlieMinerHomepage(services, options)
    }

    // South West is the only region with more than one publication
    const displayRegionSection = options.regionalTopic === 'news/south-west'
    const adTargeting = services.getAdTargeting('home', options.section, 'home')

    const sections =
        options.section === 'countryman'
            ? getCountrymanRouteInfo({ getCuratedSection: getCountrymanCuratedSection })
            : [
                  homeSegment(options),
                  subscriberExclusive(true),
                  ...billboardLeaderboardMrec('two'),
                  regionalNewsSegment(options.section),
                  waNewsSegment(options, displayRegionSection),
                  ...billboardLeaderboardMrec('three'),
              ]

    return createPageDefinition(
        {
            heading: options.title,
            hideHeading: true,
            pageType: 'static',
            pageMeta: {
                title: options.title,
                description: options.description,
            },
            socialMeta: {
                description: options.socialDescription,
            },
            noMetaTitleTemplate: true,
            adTargeting,
            section: options.section,
        },
        {
            type: 'thor',
            props: {
                hasBackgroundFill: false,
                containerWidth: metrics.thewest.mainContentWidth,
                horizontalGutters: true,
                verticalGutters: true,
            },
            contentAreas: {
                main: sections,
            },
        }
    )
}

const homepageHeroSegment = (mastheadTopic: string) =>
    layout.component({
        type: 'sierra',
        props: {
            verticalSpacing: 'gridGap',
            fixedRatios: ['16:9'],
            heroImageLayout: 'fill-container-horizontally',
            cardOrientation: {
                type: 'Landscape-Portrait',
                maxBreakpoint: 'md',
            },
            cardLayout: ['hero', 'commonWithBackground'],
            dataDefinitionArgs: {
                type: 'listing',
                topics: [mastheadTopic],
                includeSubTopics: false,
                paging: {
                    page: 1,
                    pageSize: 2,
                },
            },
        },
    })

const homepageSubHeroSegment1 = (mastheadTopic: string) =>
    layout.component({
        type: 'uniform',
        props: {
            section: 'default',
            verticalSpacing: 'lg',
            cardOrientation: { type: 'Landscape-Portrait' },
            cardLayout: [
                { type: 'november', listLength: 5 },
                ['teaserWithBackground'],
                ['noImageWithBackground', 'noImageWithBackground'],
                ['noImageWithBackground', 'noImageWithBackground'],
            ],
            dataDefinitionArgs: {
                type: 'listing',
                topics: [mastheadTopic],
                paging: {
                    page: 1,
                    pageOffset: 2,
                    pageSize: 10,
                },
            },
        },
    })

const homepageSubHeroSegment2 = (mastheadTopic: string) =>
    layout.component({
        type: 'lima',
        props: {
            verticalSpacing: 'lg',
            fixedRatios: ['16:9'],
            cardOrientation: {
                type: 'Landscape-Portrait',
            },
            finalColumns: 4,
            intermediateColumns: 2,
            hasBackground: true,
            cardLayout: [
                ['teaserWithBackground'],
                [
                    {
                        type: 'landscape',
                        format: 'landscape-paddingNoKicker',
                    },
                    {
                        type: 'landscape',
                        format: 'landscape-paddingNoKicker',
                    },
                    {
                        type: 'landscape',
                        format: 'landscape-paddingNoKicker',
                    },
                ],
                ['teaserWithBackground'],
                ['teaserWithBackground'],
            ],
            dataDefinitionArgs: {
                type: 'listing',
                topics: [mastheadTopic],
                paging: {
                    page: 1,
                    pageOffset: 12,
                    pageSize: 6,
                },
            },
        },
    })

const homeSegment = (options: RegionalHomepageOptions) => {
    const sections = [
        homepageHeroSegment(options.mastheadTopic),
        homepageSubHeroSegment1(options.mastheadTopic),
        ...billboardLeaderboardMrec('one'),
        homepageSubHeroSegment2(options.mastheadTopic),
    ]

    return layout.nestedComposition({
        type: 'thor',
        props: {
            hasBackgroundFill: false,
            containerWidth: metrics.thewest.mainContentWidth,
            verticalGutters: false,
            horizontalGutters: false,
        },
        contentAreas: {
            main: sections,
        },
    })
}

export const regionalNewsSegment = (section: TheWestSection) =>
    layout.component({
        type: 'lima',
        props: {
            fixedRatios: ['16:9'],
            verticalSpacing: 'lg',
            fontScale: 1.2,
            hasBackground: true,
            initialColumns: 1,
            intermediateColumns: 2,
            finalColumns: 4,
            cardLayout: [
                ['teaserWithBackground'],
                ['teaserWithBackground'],
                ['teaserWithBackground'],
                [
                    {
                        type: 'landscape',
                        format: 'landscape-paddingNoKicker',
                    },
                    {
                        type: 'landscape',
                        format: 'landscape-paddingNoKicker',
                    },
                    {
                        type: 'landscape',
                        format: 'landscape-paddingNoKicker',
                    },
                ],
            ],
            sectionHeader: {
                heading: 'Regional News',
                headingUrl: '',
                section,
            },
            dataDefinitionArgs: {
                type: 'curation',
                name: 'regional',
                offset: 0,
                pageSize: 6,
            },
        },
    })

export const waNewsSegment = (options: RegionalHomepageOptions, displayRegionSection: boolean) => {
    const excludeTopics = [options.mastheadTopic]
    if (displayRegionSection) {
        excludeTopics.push(options.regionalTopic)
    }
    return layout.component({
        type: 'lima',
        props: {
            verticalSpacing: 'lg',
            fixedRatios: ['16:9'],
            cardOrientation: {
                type: 'Landscape-Portrait',
            },
            finalColumns: 4,
            intermediateColumns: 2,
            hasBackground: true,
            cardLayout: [
                ['teaserWithBackground'],
                ['teaserWithBackground'],
                [
                    {
                        type: 'landscape',
                        format: 'landscape-paddingNoKicker',
                    },
                    {
                        type: 'landscape',
                        format: 'landscape-paddingNoKicker',
                    },
                    {
                        type: 'landscape',
                        format: 'landscape-paddingNoKicker',
                    },
                ],
                ['teaserWithBackground'],
            ],
            sectionHeader: {
                heading: 'WA News',
                headingUrl: '',
                section: options.section,
            },
            dataDefinitionArgs: {
                type: 'listing',
                topics: ['news/wa'],
                excludeTopics: excludeTopics,
                includeSubTopics: false,
                paging: {
                    page: 1,
                    pageSize: 6,
                },
            },
        },
    })
}
